/**
 * The Thai Hi-Lo lid's shake and lift as pure poses, offsets from where the lid
 * rests over the dice. The lift starts from wherever the shake left the lid and
 * glides it back to the centre while rising, instead of snapping it there: a
 * shake ends mid-throw (a turbo shake a fifth of the lid's width to one side),
 * and that snap read as a jolt on every round.
 */

export const SHAKE_DURATION_MS = 700;
export const LIFT_DURATION_MS = 250;

export interface VectorLidPose {
  x: number;
  y: number;
  scale: number;
}

function easeInQuad(t: number): number {
  return t * t;
}
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
export function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

/** The idle breathing scale the shake starts from fades out over this long. */
const BREATH_HANDOFF_MS = 150;

/**
 * `fromScale` is the lid's scale on the frame the shake began (the idle breathing),
 * so the first shake frame does not pop to 1.
 */
export function shakePose(elapsedMs: number, durationMs: number, radius: number, fromScale: number): VectorLidPose {
  const t = Math.min(1, elapsedMs / durationMs);
  const intensity = easeInQuad(t) * Math.max(14, radius * 0.22);
  const carried = (fromScale - 1) * Math.max(0, 1 - elapsedMs / BREATH_HANDOFF_MS);
  return {
    x: Math.sin(elapsedMs / 55) * intensity,
    y: Math.sin(elapsedMs / 45) * intensity * 0.35,
    scale: 1 + Math.sin(elapsedMs / 90) * 0.015 * easeInQuad(t) + carried,
  };
}

/** `t` runs 0..1 over the lift; `from` is the last shake pose. Ends where the lift always ended. */
export function liftPose(t: number, from: VectorLidPose, radius: number): VectorLidPose & { alpha: number } {
  const p = Math.min(1, Math.max(0, t));
  const left = 1 - easeOutCubic(p);
  return {
    x: from.x * left,
    y: from.y * left - easeOutBack(p) * radius * 1.8,
    scale: 1 + (from.scale - 1) * left,
    alpha: 1 - p * 0.9,
  };
}

// ---- the card art's motion: the whole set shaken, the lid opened on its hinge ----

/** The lid frames rendered by scripts/art/render/dice.html, closed to open. */
export const LID_FRAMES = 6;

export interface RigPose {
  x: number;
  y: number;
  rotation: number;
  scale: number;
}
export interface LidPose {
  /** Fractional lid frame: 0 closed, LID_FRAMES - 1 open; the scene blends neighbours. */
  frame: number;
  y: number;
  alpha: number;
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
function easeInCubic(t: number): number {
  return t * t * t;
}

/**
 * A dealer's shake of the closed dish: lifted off the table, thrown side to side harder and
 * harder, then set down with a thud. Offsets in frame pixels; rest at both ends, so the set
 * never jumps into or out of the shake. `frameH` is the frame's height.
 */
export function rigShakePose(elapsedMs: number, durationMs: number, frameH: number, reduced: boolean): RigPose {
  const u = Math.min(1, Math.max(0, elapsedMs / durationMs));
  const lifted = smoothstep(0, 0.16, u) * (1 - smoothstep(0.8, 0.93, u));
  const env = smoothstep(0.06, 0.4, u) * (1 - smoothstep(0.74, 0.9, u));
  const amp = frameH * (reduced ? 0.01 : 0.032);
  const thud = u > 0.92 ? Math.sin(((u - 0.92) / 0.08) * Math.PI) : 0;
  return {
    x: Math.sin(elapsedMs / 46) * amp * env,
    y: -lifted * frameH * 0.05 + Math.abs(Math.sin(elapsedMs / 37)) * amp * 0.3 * env + thud * frameH * 0.006,
    rotation: Math.sin(elapsedMs / 59) * (reduced ? 0.012 : 0.045) * env,
    scale: 1 - thud * 0.022,
  };
}

/** The lift, `t` 0..1: the lid swings open on its back rim and starts to rise. */
export function lidLiftPose(t: number, frameH: number): LidPose {
  const p = Math.min(1, Math.max(0, t));
  return { frame: easeOutCubic(p) * (LID_FRAMES - 1), y: -easeOutCubic(p) * frameH * 0.04, alpha: 1 };
}

/** After the lift, `t` 0..1: the open lid is carried up and away, fading out. */
export function lidLeavePose(t: number, frameH: number): LidPose {
  const p = Math.min(1, Math.max(0, t));
  return { frame: LID_FRAMES - 1, y: -frameH * (0.04 + easeInCubic(p) * 0.3), alpha: 1 - smoothstep(0.15, 1, p) };
}

/** Putting the lid back, `t` 0..1, from wherever it was: it comes down and shuts. */
export function lidClosePose(t: number, from: LidPose): LidPose {
  const p = Math.min(1, Math.max(0, t));
  const e = p * p;
  return { frame: from.frame * (1 - e), y: from.y * (1 - e), alpha: from.alpha + (1 - from.alpha) * Math.min(1, p * 2.5) };
}

/** Each die's start inside the settle, as a fraction of it: the last one comes late. */
const SETTLE_DELAYS = [0, 0.16, 0.4];
const SETTLE_SPAN = 0.55;

/**
 * The dice clacking into place once the lid is off, `t` 0..1 over the settle: one after
 * another a small hop with a squash on landing. In place at both ends.
 */
export function dieSettle(index: number, t: number, frameH: number): { dy: number; scaleX: number; scaleY: number } {
  const s = Math.min(1, Math.max(0, (t - (SETTLE_DELAYS[index] ?? 0)) / SETTLE_SPAN));
  if (s <= 0 || s >= 1) return { dy: 0, scaleX: 1, scaleY: 1 };
  const hop = s < 0.6 ? Math.sin((s / 0.6) * Math.PI) : 0;
  const squash = s >= 0.6 ? Math.sin(((s - 0.6) / 0.4) * Math.PI) : 0;
  return { dy: -hop * frameH * 0.03, scaleX: 1 + squash * 0.06, scaleY: 1 - squash * 0.08 };
}

/** When die `index` touches down inside the settle (0..1): the scene clacks it there. */
export function dieLandsAt(index: number): number {
  return (SETTLE_DELAYS[index] ?? 0) + SETTLE_SPAN * 0.6;
}
